// HSL bands, saturation and vibrance in OkLCh (§6.5). CPU reference for the colour shader.
import type { Vec3 } from './matrix';
import { smoothstep } from './tone';

export const HUE_BANDS = ['red', 'orange', 'yellow', 'green', 'aqua', 'blue', 'purple', 'magenta'] as const;
export type HueBand = (typeof HUE_BANDS)[number];

/**
 * OkLCh hue centres in degrees. Starting values from §6.5; tuned so the standard swatches (sRGB
 * primaries/secondaries, see tests/unit/hsl.test.ts) fall in their band: unchanged, none needed moving.
 */
export const BAND_CENTERS: Record<HueBand, number> = {
  red: 25,
  orange: 55,
  yellow: 95,
  green: 140,
  aqua: 195,
  blue: 255,
  purple: 300,
  magenta: 345,
};
const CENTERS = HUE_BANDS.map((b) => BAND_CENTERS[b]);

const wrap = (d: number) => ((((d + 180) % 360) + 360) % 360) - 180;

/** Raised-cosine weight of each band at hue `h`, reaching 0 at the neighbouring centres. Sums to 1. */
export function bandWeights(h: number): number[] {
  const w = new Array<number>(8).fill(0);
  for (let i = 0; i < 8; i++) {
    const c = CENTERS[i]!;
    const d = wrap(h - c);
    const span = d >= 0 ? wrap(CENTERS[(i + 1) % 8]! - c) : -wrap(CENTERS[(i + 7) % 8]! - c);
    const span360 = span <= 0 ? span + 360 : span;
    const t = Math.abs(d) / span360;
    w[i] = t < 1 ? 0.5 * (1 + Math.cos(Math.PI * t)) : 0;
  }
  return w;
}

export const chromaWeight = (C: number) => smoothstep(0.01, 0.06, C);

export type HslAdjust = Record<HueBand, { h: number; s: number; l: number }>;
export type ColorAdjust = { vibrance: number; saturation: number };

/** Applies HSL bands, saturation and vibrance to an OkLCh colour. */
export function adjustLch(lch: Vec3, hsl: HslAdjust | null, color: ColorAdjust): Vec3 {
  let [L, C, h] = lch;
  const cw = chromaWeight(C);
  if (hsl) {
    const w = bandWeights(h);
    let dh = 0,
      ds = 0,
      dl = 0;
    for (let i = 0; i < 8; i++) {
      const a = hsl[HUE_BANDS[i]!];
      dh += w[i]! * a.h;
      ds += w[i]! * a.s;
      dl += w[i]! * a.l;
    }
    h = (h + (dh / 100) * 30 * cw + 360) % 360;
    C *= 1 + (ds / 100) * cw;
    L += (dl / 100) * 0.12 * cw;
  }
  C *= 1 + color.saturation / 100;
  if (color.vibrance !== 0) {
    const skin = h >= 35 && h <= 75 ? 0.5 : 1;
    C *= 1 + (color.vibrance / 100) * skin * (1 - C / 0.3);
  }
  return [L, Math.max(0, C), h];
}
